import React, { useContext, useEffect, useState } from 'react';
import { MyThemeContext } from '../context/theme-context';

import { blue } from '@mui/material/colors';
import {
	Alert,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import FolderIcon from '@mui/icons-material/Folder';

import logo from '../logo.svg';
import GoogleDriveAuth from '../components/GoogleDriveAuth';
import AppNav from '../components/AppNav';
import {
	SECTEURS,
	clearGoogleDriveSession,
	loadSecteursFromDrive,
	readValidGoogleAccessToken,
	saveGoogleAccessToken,
} from '../services/googleDriveService';

const TITLE = 'Vérif Scores Quilles de Huit - Accueil';

function Home() {
	const { FRONT_END_NAME } = useContext(MyThemeContext);
	const [isAuthenticated, setIsAuthenticated] = useState(() => readValidGoogleAccessToken() !== null);
	const [secteurs, setSecteurs] = useState(SECTEURS);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [driveFolders, setDriveFolders] = useState([]);

	useEffect(() => {
		document.title = TITLE;
	}, []);

	const loadDriveData = async (accessToken) => {
		try {
			setLoading(true);
			setError(null);
			const { folders, secteurs: updatedSecteurs } = await loadSecteursFromDrive(accessToken);
			setDriveFolders(folders);
			setSecteurs(updatedSecteurs);
			setIsAuthenticated(true);
		} catch (err) {
			if (err.unauth) {
				clearGoogleDriveSession();
				setIsAuthenticated(false);
				setError('Session Google expirée ou révoquée. Reconnectez-vous.');
			} else {
				setError('Erreur lors de la récupération des fichiers Google Drive');
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const token = readValidGoogleAccessToken();
		if (token) {
			loadDriveData(token);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- restauration session au montage uniquement
	}, []);

	const handleRefreshFiles = async () => {
		const accessToken = readValidGoogleAccessToken();
		if (accessToken) {
			await loadDriveData(accessToken);
		} else {
			setIsAuthenticated(false);
			setError('Veuillez vous reconnecter à Google Drive');
		}
	};

	const handleGoogleLogout = () => {
		clearGoogleDriveSession();
		setIsAuthenticated(false);
		setSecteurs(SECTEURS);
		setDriveFolders([]);
		setError(null);
	};

	return (
		<Box>
			<AppNav />
			<Box>
				<Box sx={{ border: 1, mb: 2 }}>
					<Box sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
						<Box sx={{ pt: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
							<Avatar alt="Logo" src={logo} sx={{ m: 0, width: 100, height: 100 }} className="App-logo" />
							<Typography variant="h3" sx={{ mx: 25 }} color={blue[700]}>
								{FRONT_END_NAME}
							</Typography>
							<Avatar alt="Logo" src={logo} sx={{ m: 0, width: 100, height: 100 }} className="App-logo" />
						</Box>
					</Box>
				</Box>

				{!isAuthenticated && (
					<Box sx={{ border: 1, p: 2, mb: 2 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
							<GoogleDriveAuth
								onAuthSuccess={async ({ accessToken, expiresIn }) => {
									saveGoogleAccessToken(accessToken, expiresIn);
									setIsAuthenticated(true);
									await loadDriveData(accessToken);
								}}
							/>
						</Box>
					</Box>
				)}

				{error && (
					<Box sx={{ mb: 2 }}>
						<Alert severity="error">{error}</Alert>
					</Box>
				)}

				<Box sx={{ border: 1, p: 2 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Typography variant="h4" color={blue[700]}>
							Analyse des fichiers par secteur
						</Typography>
						{isAuthenticated && (
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button
									variant="contained"
									startIcon={<CloudIcon />}
									color="primary"
									onClick={handleRefreshFiles}
									disabled={loading}
								>
									{loading ? <CircularProgress size={24} /> : 'Rafraîchir les fichiers'}
								</Button>
								<Button variant="outlined" color="secondary" onClick={handleGoogleLogout} disabled={loading}>
									Déconnexion Drive
								</Button>
							</Box>
						)}
					</Box>
					<TableContainer component={Paper}>
						<Table sx={{ minWidth: 650 }} aria-label="tableau des secteurs">
							<TableHead>
								<TableRow>
									<TableCell>Secteur</TableCell>
									<TableCell>Dossier Drive</TableCell>
									<TableCell>Fichier</TableCell>
									<TableCell>Date</TableCell>
									<TableCell>Taille</TableCell>
									<TableCell>Statut</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{secteurs.map((secteur) => (
									<TableRow key={secteur.id}>
										<TableCell component="th" scope="row">
											{secteur.nom}
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<FolderIcon color="primary" />
												{driveFolders.find((f) => f.name.includes(secteur.path))?.name || '—'}
											</Box>
										</TableCell>
										<TableCell>{secteur.fichier}</TableCell>
										<TableCell>{secteur.date}</TableCell>
										<TableCell>{secteur.taille ?? '—'}</TableCell>
										<TableCell>{secteur.statut}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Box>
		</Box>
	);
}

export default Home;
